import { useLoaderData, useLocation } from 'react-router';
import { CmsHeader } from './header/cms-header';
import { CmsSidebar } from './sidebar/cms-sidebar';
import { useAuthStore } from '@/features/auth/auth.store';
import { Suspense, useEffect } from 'react';
import { Loader } from '@/components/ui/loader';
import { RouteErrorBoundary } from './route-error-boundary';
import { CompanySwitchBanner } from './company-switch-banner';
import { ViewAsBanner } from './view-as-banner';
import { CompanyScopedOutlet } from './company-scoped-outlet';
import { SectionTabsFrame } from './section-tabs';
import { useCompanyStore } from '@/stores/company.store';

const CmsLayout = () => {
  const setPermissions = useAuthStore(state => state.setPermissions);
  const setUser = useAuthStore(state => state.setUser);
  const { user, permissions } = useLoaderData();
  const { pathname } = useLocation();
  // Re-renders the sidebar and header (not a remount: their state stays) once
  // a tenant switch has torn the cache down. Their queries for the new tenant
  // start during the switch render and are cancelled and cleared by that
  // teardown; nothing else re-renders them, so their observers would sit on
  // the dead queries and, e.g., the flag-gated menus read off until a reload
  // (ELI-374). A re-render makes every `useQuery` there re-attach and refetch.
  useCompanyStore(state => state.scopeEpoch);

  useEffect(() => {
    setPermissions(permissions);
    setUser(user);
  }, [permissions, setPermissions, user, setUser]);

  // Reset scroll to the top on every route change (React Router keeps the
  // previous scroll position by default). Keyed on pathname only so in-page
  // query/tab changes are left untouched.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pathname]);

  return (
    <div className="bg-neutral-90 flex min-h-screen flex-col">
      <ViewAsBanner />
      <CompanySwitchBanner />

      <div className="flex flex-1">
        <CmsSidebar />

        {/* `overflow-x-clip` instead of `hidden`: clipping without creating a
            scroll container, so `position: sticky` children (e.g. the planboard
            scrollbar) still resolve against the document scroll.
            `min-w-0` is required — flexbox only zeroes a flex item's automatic
            minimum size when it is a scroll container, which `clip` is not.
            Without it this column refuses to shrink, collapsing the sidebar to
            0px and pushing the document into horizontal scroll. */}
        <div className="flex min-w-0 flex-1 flex-col space-y-6 overflow-x-clip md:p-6">
          <CmsHeader />

          <main className="flex-1">
            <div className="h-full min-w-full">
              {/* Outside the Suspense, so the tabs stay put while the next
                  tab's page loads. */}
              <SectionTabsFrame>
                <RouteErrorBoundary>
                  <Suspense
                    fallback={
                      <div className="flex h-full w-full items-center justify-center">
                        <Loader />
                      </div>
                    }
                  >
                    <CompanyScopedOutlet />
                  </Suspense>
                </RouteErrorBoundary>
              </SectionTabsFrame>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default CmsLayout;
